import { Fragment } from 'react';
import { Button, Dropdown, Popconfirm, Radio, Tag, Tooltip } from 'antd';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isEmpty } from 'lodash';
import { convertDateTimeRangeToLocalText } from '@utils/utils';

/** 会议/日程拖拽手柄（仅在手柄上绑定 listeners，与原先 useDragHandle 行为一致） */
export function HallDragHandleButton({ listeners }) {
    return (
        <Button type="text" size="small" {...listeners}>
            <span className="anticon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrows-move" viewBox="0 0 16 16">
                    <path
                        fillRule="evenodd"
                        d="M7.646.146a.5.5 0 0 1 .708 0l2 2a.5.5 0 0 1-.708.708L8.5 1.707V5.5a.5.5 0 0 1-1 0V1.707L6.354 2.854a.5.5 0 1 1-.708-.708l2-2zM8 10a.5.5 0 0 1 .5.5v3.793l1.146-1.147a.5.5 0 0 1 .708.708l-2 2a.5.5 0 0 1-.708 0l-2-2a.5.5 0 0 1 .708-.708L7.5 14.293V10.5A.5.5 0 0 1 8 10zM.146 8.354a.5.5 0 0 1 0-.708l2-2a.5.5 0 1 1 .708.708L1.707 7.5H5.5a.5.5 0 0 1 0 1H1.707l1.147 1.146a.5.5 0 0 1-.708.708l-2-2zM10 8a.5.5 0 0 1 .5-.5h3.793l-1.147-1.146a.5.5 0 0 1 .708-.708l2 2a.5.5 0 0 1 0 .708l-2 2a.5.5 0 0 1-.708-.708L14.293 8.5H10.5A.5.5 0 0 1 10 8z"
                    />
                </svg>
            </span>
        </Button>
    );
}

/** 二级会议行（嵌套 SortableContext） */
export function SortableNestedHallRow(props) {
    const { subNode, laborFeeConfirmed, staffs, currentConferenceHall, canChangeConfHallDate, dateList, showAddConferenceHallDrawer, changeConferenceHallStatus, deleteConference, checkConference, showChangeConferenceDateModal } = props;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: subNode.id,
        disabled: laborFeeConfirmed,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : undefined,
    };

    const items = [
        {
            label: '编辑会议',
            key: 'editConferenceHall',
            onClick: () => showAddConferenceHallDrawer(subNode),
        },
        {
            label: subNode.hidden ? '显示会议' : '隐藏会议',
            key: 'changeConferenceHallDisplayStatus',
            onClick: () =>
                changeConferenceHallStatus({
                    id: subNode.id,
                    hidden: subNode.hidden ? 0 : 1,
                }),
        },
        ...(canChangeConfHallDate && dateList.length > 1
            ? [
                  {
                      label: '调整会议日期',
                      key: 'changeConferenceHallDate',
                      onClick: () => showChangeConferenceDateModal(subNode),
                  },
              ]
            : []),
        {
            label: '删除会议',
            key: 'delConferenceHall',
            onClick: () => deleteConference(subNode.id),
            danger: true,
        },
    ];

    const dateTimesArr = subNode.agendasTime?.split(/-(?=\d{4})/) || [];
    let dateTimeRange = '';
    if (dateTimesArr.length === 2) {
        dateTimeRange = convertDateTimeRangeToLocalText(dateTimesArr[0], dateTimesArr[1], 0);
    }

    let directors = '';
    const directorsStr = subNode.directors;
    if (!!directorsStr) {
        const directorArr = directorsStr.split(',');
        const directorNum = directorArr.length;
        if (directorNum > 0 && !isEmpty(staffs)) {
            let directorName = '';
            const directorId = Number(directorArr[0]) || 0;
            directorName = staffs.find(s => s.id === directorId)?.name || '';
            if (directorNum === 1) {
                directors = directorName;
            }
            if (directorNum > 1) {
                directors = `${directorName}等${directorNum}人`;
            }
        }
    }

    return (
        <div ref={setNodeRef} style={style} {...attributes} className={`halls-hall sub-hall ${subNode.id === currentConferenceHall?.id && 'hall-selected'}`}>
            <div className="hall-info">
                <div className="hall-title-box">
                    <div className="hall-radio">
                        <Tooltip title="选择会议场次管理其下日程">
                            <Radio checked={subNode.id === currentConferenceHall?.id} style={{ marginLeft: 4 }} onClick={() => checkConference(subNode)} />
                        </Tooltip>
                    </div>

                    <div className="hall-title" title={subNode.name} onClick={() => checkConference(subNode)}>
                        <span>
                            {subNode.name}
                            <small className="id">YHYC#{subNode.id}</small>
                        </span>
                    </div>
                </div>
                <div className="hall-extra" onClick={() => checkConference(subNode)}>
                    {(!!dateTimeRange || !!directors) && (
                        <div className="hall-extra-info">
                            {!!dateTimeRange && (
                                <span className="time">
                                    <label>时间：</label>
                                    {dateTimeRange}
                                </span>
                            )}
                            {!!directors && (
                                <span className="directors">
                                    <label>项目负责人：</label>
                                    {directors}
                                </span>
                            )}
                        </div>
                    )}

                    {!!subNode?.place && (
                        <div className="hall-extra-info">
                            <span className="place">
                                <label>地点：</label>
                                {subNode.place}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <Popconfirm title={!!subNode.hidden ? '确定显示？' : '确定隐藏？'} okText="确定" cancelText="取消" onConfirm={() => changeConferenceHallStatus({ id: subNode.id, hidden: subNode.hidden ? 0 : 1 })}>
                <Tag color={!!subNode.hidden ? 'warning' : 'green'} style={{ marginLeft: 4, marginRight: 4, width: 54, textAlign: 'center', cursor: 'pointer' }}>
                    {!!subNode.hidden ? '隐藏中' : '显示中'}
                </Tag>
            </Popconfirm>

            {!laborFeeConfirmed && (
                <div className="hall-operations">
                    <Dropdown menu={{ items }} placement="bottomRight">
                        <Button size="small" type="text" className="more-operation">
                            <span role="img" aria-label="holder" className="anticon">
                                <i className="bi bi-three-dots"></i>
                            </span>
                        </Button>
                    </Dropdown>

                    <HallDragHandleButton listeners={listeners} />
                </div>
            )}
        </div>
    );
}

/** 二级会议拖拽区域 */
export function SortableNestedHallsList({ parentId, childItems, dndSensors, onDragEndNestedHalls, nestedRowProps }) {
    const ids = (childItems || []).map(c => c.id);
    if (!childItems?.length) {
        return null;
    }
    return (
        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEndNestedHalls(parentId)}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <div className="hall-wrap">
                    {childItems.map(subNode => (
                        <SortableNestedHallRow key={subNode.id} subNode={subNode} {...nestedRowProps} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

/** 一级会议行 */
export function SortableMainHallRow(props) {
    const { treeNode, laborFeeConfirmed, staffs, currentConferenceHall, canChangeConfHallDate, dateList, showAddConferenceHallDrawer, changeConferenceHallStatus, deleteConference, checkConference, showChangeConferenceDateModal, dndSensors, onDragEndNestedHalls } = props;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: treeNode.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : undefined,
    };

    const nestedRowProps = {
        laborFeeConfirmed,
        staffs,
        currentConferenceHall,
        canChangeConfHallDate,
        dateList,
        showAddConferenceHallDrawer,
        changeConferenceHallStatus,
        deleteConference,
        checkConference,
        showChangeConferenceDateModal,
    };

    let items = [
        {
            label: '编辑会议',
            key: 'editConferenceHall',
            onClick: () => showAddConferenceHallDrawer(treeNode),
        },
        {
            label: treeNode.hidden ? '显示会议' : '隐藏会议',
            key: 'changeConferenceHallDisplayStatus',
            onClick: () =>
                changeConferenceHallStatus({
                    id: treeNode.id,
                    hidden: treeNode.hidden ? 0 : 1,
                }),
        },
    ];
    if (!treeNode.secondHallsList || (treeNode.secondHallsList && !treeNode.secondHallsList.length)) {
        items = [
            ...items,
            ...(canChangeConfHallDate && dateList.length > 1
                ? [
                      {
                          label: '调整会议日期',
                          key: 'changeConferenceHallDate',
                          onClick: () => showChangeConferenceDateModal(treeNode),
                      },
                  ]
                : []),
            {
                label: '删除会议',
                key: 'delConferenceHall',
                onClick: () => deleteConference(treeNode.id),
                danger: true,
            },
        ];
    }

    const dateTimesArr = treeNode.agendasTime?.split(/-(?=\d{4})/) || [];
    let dateTimeRange = '';
    if (dateTimesArr.length === 2) {
        dateTimeRange = convertDateTimeRangeToLocalText(dateTimesArr[0], dateTimesArr[1], 0);
    }

    let directors = '';
    const directorsStr = treeNode.directors;
    if (!!directorsStr) {
        const directorArr = directorsStr.split(',');
        const directorNum = directorArr.length;
        if (directorNum > 0 && !isEmpty(staffs)) {
            let directorName = '';
            const directorId = Number(directorArr[0]) || 0;
            directorName = staffs.find(s => s.id === directorId)?.name || '';
            if (directorNum === 1) {
                directors = directorName;
            }
            if (directorNum > 1) {
                directors = `${directorName}等${directorNum}人`;
            }
        }
    }

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className={`halls-hall ${treeNode.id === currentConferenceHall?.id && 'hall-selected'}`}>
                <div className="hall-info">
                    <div className="hall-title-box">
                        <div className="hall-radio">
                            {!treeNode.secondHallsList || (treeNode.secondHallsList && !treeNode.secondHallsList.length) ? (
                                <Tooltip title="选择会议，管理其下日程">
                                    <Radio checked={treeNode.id === currentConferenceHall?.id} style={{ marginLeft: 4 }} onClick={() => checkConference(treeNode)} />
                                </Tooltip>
                            ) : null}
                        </div>

                        <div className="hall-title" title={treeNode.name}>
                            <span onClick={() => checkConference(treeNode)}>
                                {treeNode.name}
                                <small className="id">YHYC#{treeNode.id}</small>
                            </span>
                        </div>
                    </div>
                    <div className="hall-extra" onClick={() => checkConference(treeNode)}>
                        <div className="hall-extra-info">
                            {!!dateTimeRange && (
                                <span className="time">
                                    <label>时间：</label>
                                    {dateTimeRange}
                                </span>
                            )}
                            {!!directors && (
                                <span className="directors">
                                    <label>项目负责人：</label>
                                    {directors}
                                </span>
                            )}
                        </div>
                        {!!treeNode?.place && (
                            <div className="hall-extra-info">
                                <span className="place">
                                    <label>地点：</label>
                                    {treeNode.place}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <Popconfirm title={!!treeNode.hidden ? '确定显示？' : '确定隐藏？'} okText="确定" cancelText="取消" onConfirm={() => changeConferenceHallStatus({ id: treeNode.id, hidden: treeNode.hidden ? 0 : 1 })}>
                    <Tag color={!!treeNode.hidden ? 'warning' : 'green'} style={{ marginLeft: 4, marginRight: 4, width: 54, textAlign: 'center', cursor: 'pointer' }}>
                        {!!treeNode.hidden ? '隐藏中' : '显示中'}
                    </Tag>
                </Popconfirm>

                <div className="hall-operations">
                    <Dropdown menu={{ items }} placement="bottomRight">
                        <Button size="small" type="text" className="more-operation">
                            <span className="anticon">
                                <i className="bi bi-three-dots"></i>
                            </span>
                        </Button>
                    </Dropdown>
                    <HallDragHandleButton listeners={listeners} />
                </div>
            </div>

            <SortableNestedHallsList parentId={treeNode.id} childItems={treeNode.secondHallsList} dndSensors={dndSensors} onDragEndNestedHalls={onDragEndNestedHalls} nestedRowProps={nestedRowProps} />
        </div>
    );
}

/** 右侧日程行 */
export function AgendaSortableRow({ agendasItem, getSortModeName, showAddAgendaDrawer, changeAgendaAndGuestsStatus, deleteAgenda }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: agendasItem.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : undefined,
    };

    const items = [
        {
            label: '编辑日程',
            key: 'editAgenda',
            onClick: () => showAddAgendaDrawer(agendasItem),
        },
        {
            label: agendasItem.hidden ? '显示日程' : '隐藏日程',
            key: 'changeAgendaDisplayStatus',
            onClick: () =>
                changeAgendaAndGuestsStatus('agenda_status', {
                    id: agendasItem.id,
                    hidden: agendasItem.hidden ? 0 : 1,
                }),
        },
        {
            label: agendasItem.guestsHidden ? '显示日程嘉宾' : '隐藏日程嘉宾',
            key: 'changeAgendaGuestsDisplayStatus',
            onClick: () =>
                changeAgendaAndGuestsStatus('guests_status', {
                    id: agendasItem.id,
                    guestsHidden: agendasItem.guestsHidden ? 0 : 1,
                }),
        },
        {
            label: '删除日程',
            key: 'deleteAgenda',
            onClick: () => deleteAgenda(agendasItem.id),
            danger: true,
        },
    ];

    return (
        <div ref={setNodeRef} style={style} {...attributes} className="agendas-agenda">
            <div className="agenda-info">
                <div className="agenda-datetime">
                    <span className="anticon" style={{ marginRight: 4 }}>
                        <i className="bi bi-clock"></i>
                    </span>
                    {agendasItem?.startTime}&ndash;{agendasItem?.endTime}
                </div>
                <div className="agenda-title">
                    {agendasItem.title}
                    <Popconfirm
                        title={!!agendasItem.hidden ? '确定显示？' : '确定隐藏？'}
                        okText="确定"
                        cancelText="取消"
                        onConfirm={() =>
                            changeAgendaAndGuestsStatus('agenda_status', {
                                id: agendasItem.id,
                                hidden: agendasItem.hidden ? 0 : 1,
                            })
                        }
                    >
                        <Tag color={!!agendasItem.hidden ? 'warning' : 'green'} style={{ marginLeft: 10, marginRight: 0, textAlign: 'center', cursor: 'pointer' }}>
                            {!!agendasItem.hidden ? '日程隐藏中' : '日程显示中'}
                        </Tag>
                    </Popconfirm>

                    {!!agendasItem.guestList?.length && (
                        <Popconfirm
                            title={!!agendasItem.guestsHidden ? '确定显示？' : '确定隐藏？'}
                            okText="确定"
                            cancelText="取消"
                            onConfirm={() =>
                                changeAgendaAndGuestsStatus('guests_status', {
                                    id: agendasItem.id,
                                    guestsHidden: agendasItem.guestsHidden ? 0 : 1,
                                })
                            }
                        >
                            <Tag color={!!agendasItem.guestsHidden ? 'warning' : 'green'} style={{ marginLeft: 10, marginRight: 0, textAlign: 'center', cursor: 'pointer' }}>
                                {!!agendasItem.guestsHidden ? '嘉宾隐藏中' : '嘉宾显示中'}
                            </Tag>
                        </Popconfirm>
                    )}
                </div>

                {!!agendasItem.discussionList?.length && agendasItem.discussionList.map(discuss => <li key={String(discuss)}>{discuss}</li>)}

                <div className="agenda-foot">
                    <div className="agenda-guests">
                        {!!agendasItem.guestList?.length && (
                            <>
                                嘉宾{!!agendasItem.sortMode && `（${getSortModeName(agendasItem.sortMode)}）`}：
                                {agendasItem.guestList.map(guest => {
                                    const invitationAcceptanceState = { 0: '未反馈', 1: '已接受', 2: '已拒绝' };
                                    const agendaGuest = agendasItem?.agendaGuestsList?.find(item => item.expertId === guest.id) || {};
                                    return (
                                        <Fragment key={guest.id}>
                                            <span
                                                className="guest"
                                                style={{
                                                    marginRight: agendaGuest.invitationAccepted > 0 ? 0 : 10,
                                                    color: agendaGuest.invitationAccepted === 1 ? '#389e0d' : agendaGuest.invitationAccepted === 2 ? '#ff4d4f' : '',
                                                }}
                                            >
                                                {guest.memUsername}
                                            </span>
                                            {!isEmpty(agendaGuest) && agendaGuest.invitationAccepted > 0 && <Tag color={agendaGuest.invitationAccepted === 1 ? 'green' : 'error'}>{invitationAcceptanceState[agendaGuest.invitationAccepted]}</Tag>}
                                        </Fragment>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="agenda-operations">
                <Dropdown menu={{ items }} placement="bottomRight">
                    <Button size="small" type="text" className="more-operation">
                        <span role="img" aria-label="holder" className="anticon">
                            <i className="bi bi-three-dots"></i>
                        </span>
                    </Button>
                </Dropdown>

                <HallDragHandleButton listeners={listeners} />
            </div>
        </div>
    );
}
